/**
 * Per-campaign Shopify coupon data layer (config + coupons), wired to the new
 * BE namespace `/api/v1/campaigns/{id}/shopify/*`:
 *   GET  /campaigns/{id}/shopify                    → config + connection + counts
 *   PUT  /campaigns/{id}/shopify                    → save config
 *   GET  /campaigns/{id}/shopify/coupons            → list coupons
 *   POST /campaigns/{id}/shopify/coupons/generate        → bulk mint (idempotent)
 *   POST /campaigns/{id}/shopify/coupons/invalidate      → deactivate every code, per-code result
 *   POST /campaigns/{id}/shopify/coupons/invalidate-many → invalidate a selected subset
 *   DELETE /campaigns/{id}/shopify/coupons/{id}          → invalidate one
 *   DELETE /campaigns/{id}/shopify/coupons/{id}/delete     → permanently delete (campaign-scope only)
 *   POST /campaigns/{id}/shopify/coupons/{id}/reactivate → reactivate an invalidated campaign-wide coupon
 *   POST /campaigns/{id}/shopify/setup-again             → re-open a campaign whose Shopify data was removed
 *
 * Self-contained (own types + apiClient calls) so the per-campaign Shopify tab
 * can ship without threading through the connection/per-roster factories.
 */
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient, ApiError } from '@discovr/common';
import {
  editConfigFailure,
  editSavedMessage,
  errorMessage,
  generateResultMessage,
  generateReviewCopy,
  invalidateManyMessage,
  refreshResultMessage,
  shopifyDeactivateAllCopy,
  deactivateAllMessage,
  renamedConflictMessage,
  shopifyCopy,
  shopifyEditCopy,
  dateRuleMessage,
  creatorLiveCode,
  renameFailure,
  shopifyRenameCopy,
} from '../../copy';
import type { OutcomeMessage } from '../../copy';
import type {
  DataResponse,
  ShopifyDuplicateSources,
  ShopifySetupAgain,
  ShopifySetupAgainResult,
  ShopifyStoreState,
} from '@discovr/types';
import { campaignKeys } from '../../campaign-detail/queryKeys';
import { shopifyKeys } from '../queryKeys';
import { clearShopifyDraft } from '../draftStorage';
import { refreshCampaignShopify } from './shopifyCouponsRefresh';
import type { ShopifyDiscountKindTerms } from '../discountKind';

export type ShopifyMode = 'per_creator' | 'campaign_wide';

/** A product the campaign coupon targets (empty list = all products). */
export interface ShopifyProductRef {
  id: string; // gid://shopify/Product/<n>
  handle: string;
  title: string;
}

/** A row from GET /campaigns/{id}/shopify/products (picker search results). */
export interface ShopifyProductSummary {
  id: string;
  handle: string;
  title: string;
  image_url: string;
  /** minVariantPrice.amount — a "from" price for a multi-variant product.
   *  Empty when Shopify has no price data. */
  price?: string;
  /** Summed available quantity across every variant and location. Absent
   *  when the product doesn't track inventory (distinct from a tracked 0,
   *  "out of stock"). */
  total_inventory?: number;
  /** false ⇒ Shopify sells the product without counting stock, so
   *  total_inventory says nothing about availability. */
  tracks_inventory?: boolean;
  /** A variant keeps selling at zero stock (inventoryPolicy CONTINUE). */
  oversell_allowed?: boolean;
  /** More variants than the server read and none of them oversells: unknown. */
  oversell_unknown?: boolean;
  /** false ⇒ the product has real variants the picker can offer. */
  has_only_default_variant?: boolean;
}

/** A specific variant the coupon targets, named with its product. */
export interface ShopifyVariantRef {
  id: string; // gid://shopify/ProductVariant/<n>
  title: string;
  product_id: string;
  product_title: string;
  product_handle: string;
}

/** A row from GET .../products/{product_id}/variants. */
export interface ShopifyVariantSummary {
  id: string;
  title: string;
  price?: string;
  inventory_quantity?: number;
  /** false ⇒ Shopify sells the variant without counting stock. */
  tracks_inventory?: boolean;
  /** The variant keeps selling at zero stock (inventoryPolicy CONTINUE). */
  oversell_allowed?: boolean;
}

export interface ShopifyVariantPage {
  variants: ShopifyVariantSummary[] | null;
  has_next_page: boolean;
  end_cursor: string;
}

/** GET /campaigns/{id}/shopify/products — one page of search results. */
export interface ShopifyProductPage {
  products: ShopifyProductSummary[] | null;
  has_next_page: boolean;
  end_cursor: string;
}

/** What the campaign's discount applies to — the "Applies to" selector.
 *  BE normalizes empty/legacy configs to 'product'. */
export type ShopifyTargetType = 'product' | 'collection' | 'entire_store';

/** A collection the campaign coupon targets (single-select, like products). */
export interface ShopifyCollectionRef {
  id: string; // gid://shopify/Collection/<n>
  handle: string;
  title: string;
}

/** A row from GET /campaigns/{id}/shopify/collections (picker search results). */
export interface ShopifyCollectionSummary {
  id: string;
  handle: string;
  title: string;
  image_url: string;
}

/** GET /campaigns/{id}/shopify/collections — one page of search results. */
export interface ShopifyCollectionPage {
  collections: ShopifyCollectionSummary[] | null;
  has_next_page: boolean;
  end_cursor: string;
}

/** A row of GET /campaigns/{id}/shopify/creators — the "choose your creators"
 *  picker (the finalized-creator set Generate would mint for). */
export interface ShopifyPickerCreator {
  roster_entry_id: string;
  display_name: string;
  handle?: string;
  avatar_url?: string;
  platform?: string;
  /** Already has a live (non-revoked) coupon — Generate would skip them. */
  has_coupon: boolean;
  /** A post of this creator is on hold, so Generate skips them. Admin responses only. */
  on_hold?: boolean;
  /** Shopify renamed this creator's code; Generate skips them. */
  has_renamed_coupon?: boolean;
  /** The creator's only code is deactivated; Generate skips them. */
  has_inactive_coupon?: boolean;
  /** Already posted: their published content doesn't carry the code. Generate still includes them. */
  live?: boolean;
  /** The live state couldn't be read; `live` is false on every row then. */
  live_unknown?: boolean;
}

/** minimum_requirement_type on CampaignShopifyConfig — "" means no minimum. */
export type ShopifyMinimumRequirementType = '' | 'amount' | 'quantity';

/** value_type on CampaignShopifyConfig / ShopifyCouponTerms — "" (unset, every
 *  pre-existing config/coupon) means percentage, same as explicit 'percentage'. */
export type ShopifyDiscountValueType = '' | 'percentage' | 'fixed_amount';
/** Which purchases a discount applies to; "" reads as one_time. */
export type ShopifyPurchaseType = '' | 'one_time' | 'subscription' | 'both';

/** GET /campaigns/{id}/shopify */
export interface CampaignShopifyConfig extends ShopifyDiscountKindTerms {
  enabled: boolean;
  mode: ShopifyMode | string;
  /** Per-creator mode: mint a code when a creator is confirmed. Absent on older responses = false. */
  auto_create_for_new_creators?: boolean;
  /** Brand-typed human-readable name the code is derived from, e.g. "Diwali
   *  Sale" -> DIWALISALE_VIRATKOHLI15. Replaces the old code_prefix/code_base
   *  split. */
  coupon_name: string;
  /** "" ⇒ percentage (every pre-existing config). discount_pct is ignored when
   *  fixed_amount; discount_amount is ignored when percentage. */
  value_type: ShopifyDiscountValueType;
  discount_pct: number;
  discount_amount: number;
  include_pct_in_code: boolean;
  target_type: ShopifyTargetType;
  products: ShopifyProductRef[];
  /** Specific variants, with or instead of whole products. */
  variants: ShopifyVariantRef[];
  collections: ShopifyCollectionRef[];
  landing_path: string;
  /** "" ⇒ no minimum; "amount" ⇒ ₹ subtotal threshold; "quantity" ⇒ item-count
   *  threshold. minimum_requirement_value is ignored when this is "". */
  minimum_requirement_type: ShopifyMinimumRequirementType;
  minimum_requirement_value: number;
  combines_with_order_discounts: boolean;
  combines_with_product_discounts: boolean;
  combines_with_shipping_discounts: boolean;
  /** RFC 3339 instants, minute precision. Both optional and independent —
   *  either may be set alone. When both are set, end_date must be strictly
   *  after start_date. The form shows and edits them in the store's zone
   *  (CampaignShopifyConfig.store_timezone). */
  start_date?: string;
  end_date?: string;
  /** Total redemptions allowed across every customer — undefined/0 means
   *  unlimited (Shopify's own default). */
  usage_limit?: number;
  /** Restricts each customer to a single redemption, independent of
   *  usage_limit. */
  applies_once_per_customer: boolean;
  /** Fixed amount only: true takes the amount off each eligible item, false
   *  once per order. Always false for a percentage. */
  applies_on_each_item: boolean;
  purchase_type: ShopifyPurchaseType;
  /** Subscription payments discounted; 0 = all. 0 for one_time. */
  recurring_cycle_limit: number;
  connected: boolean;
  shop_domain: string;
  /** The connected store's base currency (ISO 4217, e.g. "INR" / "USD") — the
   *  currency discount_amount and minimum_requirement_value are entered in,
   *  since Shopify accepts a fixed amount only in the store's default currency
   *  and converts it to the buyer's currency at checkout. Best-effort: absent
   *  when nothing is connected or the live shop read failed. */
  currency_code?: string;
  /** The store's IANA time zone (e.g. "America/New_York") — the zone coupon
   *  dates are shown and edited in. Omitted when unknown; the FE then uses UTC. */
  store_timezone?: string;
  coupon_count: number;
  live_coupon_count: number;
  /** Server-computed: how many finalized creators still lack a live coupon. */
  pending_creators_count: number;
  /** Server-computed: Generate would mint at least one code right now. The FE
   *  disables the Generate button off this — never re-derive it client-side. */
  can_generate: boolean;
  /** Server-computed; never derive it from shop_domain. */
  store_state: ShopifyStoreState;
  /** Server-computed: store not connected, unavailable or removed ⇒ every coupon mutation 409s.
   *  The FE puts the whole management surface in view-only off this one flag —
   *  writes are HIDDEN, not disabled, so the set can't drift (item 123). */
  coupons_read_only: boolean;
  /** Connected store that Shopify reports frozen, closed or paused; coupons_read_only is also true. */
  store_frozen?: boolean;
  /** Server-computed: whether a removed campaign can be set up again. */
  setup_again: ShopifySetupAgain;
  previous_removed_at?: string;
  setup_again_at?: string;
}

/** PUT /campaigns/{id}/shopify body. */
export interface CampaignShopifyConfigInput extends ShopifyDiscountKindTerms {
  mode: string;
  auto_create_for_new_creators: boolean;
  coupon_name: string;
  value_type: ShopifyDiscountValueType;
  discount_pct: number;
  discount_amount: number;
  include_pct_in_code: boolean;
  target_type: ShopifyTargetType;
  /** One or more products, when target_type='product' (multi-select). */
  products: ShopifyProductRef[];
  /** Specific variants, with or instead of whole products. */
  variants: ShopifyVariantRef[];
  /** One or more collections, when target_type='collection' (multi-select). */
  collections: ShopifyCollectionRef[];
  minimum_requirement_type: ShopifyMinimumRequirementType;
  minimum_requirement_value: number;
  combines_with_order_discounts: boolean;
  combines_with_product_discounts: boolean;
  combines_with_shipping_discounts: boolean;
  start_date?: string;
  end_date?: string;
  usage_limit?: number;
  applies_once_per_customer: boolean;
  applies_on_each_item: boolean;
  purchase_type: ShopifyPurchaseType;
  recurring_cycle_limit: number;
}

/** The discount terms a coupon was actually minted under — a snapshot, not
 *  the campaign's current config. Settings saved after this coupon existed
 *  don't apply to it retroactively (each settings change mints a fresh
 *  Shopify discount for creators generated from that point on; existing
 *  coupons keep pointing at their original one), so this is the source of
 *  truth for "what does this specific coupon actually do." Always present —
 *  every coupon is minted through Generate, which always builds one. */
export interface ShopifyCouponTerms extends ShopifyDiscountKindTerms {
  coupon_name: string;
  value_type?: ShopifyDiscountValueType;
  discount_pct: number;
  discount_amount?: number;
  include_pct_in_code: boolean;
  target_type: ShopifyTargetType;
  products?: { id: string; title: string; image_url?: string }[];
  variants?: { id: string; title: string; product_id: string; product_title: string }[];
  collections?: { id: string; title: string; image_url?: string }[];
  minimum_requirement_type?: ShopifyMinimumRequirementType;
  minimum_requirement_value?: number;
  combines_with_order_discounts: boolean;
  combines_with_product_discounts: boolean;
  combines_with_shipping_discounts: boolean;
  start_date?: string;
  end_date?: string;
  usage_limit?: number;
  applies_once_per_customer: boolean;
  applies_on_each_item?: boolean;
  purchase_type?: ShopifyPurchaseType;
  recurring_cycle_limit?: number;
  /** The store's base currency at the moment this coupon was minted — keeps a
   *  fixed-amount coupon rendering as the money it actually is even after the
   *  brand connects a different store. Absent on coupons minted before this
   *  was captured; callers fall back via resolveCurrencyCode. */
  currency_code?: string;
}

/** A row of GET /campaigns/{id}/shopify/coupons. */
export interface ShopifyCampaignCoupon {
  id: string;
  scope: string; // "creator" | "campaign"
  code: string;
  tracking_link: string;
  status: string; // "assigned" | "orphaned" | "invalid" | "deleted"
  /** The coupon's headline discount value — the percentage, or (for a
   *  fixed_amount coupon, see terms.value_type) the ₹ amount off. */
  discount_pct: number;
  user_id?: string;
  roster_entry_id?: string;
  creator_name?: string;
  creator_handle?: string;
  creator_avatar?: string;
  /** This creator's main deliverable is currently live (roster_posts "live") —
   *  independent of the coupon's own assigned/orphaned/invalid status. */
  creator_live: boolean;
  /** A post of this creator is on hold. Status only — the coupon is unchanged. */
  creator_on_hold?: boolean;
  /** Post status of the creator's least advanced post, e.g. "content_review". */
  creator_stage?: string;
  /** The store this coupon was minted against. */
  shop_domain: string;
  /** Shopify's own per-code usage count, refreshed periodically (see
   *  uses_fetched_at). Null until it has been fetched once. */
  uses_count: number | null;
  uses_fetched_at: string | null;
  /** Codes on the native Shopify discount, as the sync last read them. */
  native_codes_count?: number;
  /** Deep link to this discount in Shopify Admin; absent until the BE can build it. */
  shopify_admin_url?: string;
  /** Paid-family orders attributed to this coupon (best-effort, may be 0 even
   *  for a live coupon if the usage rollup couldn't be computed). */
  paid_orders_count: number;
  /** See ShopifyCouponTerms. */
  terms: ShopifyCouponTerms;
  /** How many creators are finalized on this campaign — set ONLY on
   *  scope === 'campaign' rows (a campaign-wide coupon is, by construction,
   *  the one code every finalized creator shares). Backs the Delete/
   *  Invalidate confirmation dialogs' "this code is shared by N creators"
   *  warning. Absent/0 for creator-scope rows. */
  creator_count?: number;
  /** Whether Shopify accepts the code right now, from the dates synced from
   *  Shopify. A deactivation in Shopify admin reads as "expired". */
  shopify_state: ShopifyCouponState;
  /** Opaque token for the coupon's state and dates; the edit modal sends the
   *  value it opened with back as `expected_version`. */
  edit_version?: string;
  /** Shopify's use count has reached the total usage limit: checkout refuses
   *  the code although `shopify_state` is still active. */
  usage_limit_reached?: boolean;
  /** A turned-off or replaced code that Shopify still accepts (reactivated
   *  there); its sales are credited. */
  native_active?: boolean;
  /** History fields (ISO), supplied by the BE for deactivated/replaced rows. */
  deactivated_at?: string | null;
  replaced_at?: string | null;
  replaced_by_code?: string | null;
  /** The merchant changed this discount in Shopify into something Discovr
   *  can't edit; it is shown read-only. */
  managed_in_shopify?: boolean;
  /** The code Shopify now holds in place of this one. Set means the row is not
   *  live and orders on the new code are not credited to it. */
  shopify_renamed_to?: string;
  shopify_renamed_at?: string;
  /** Shopify's own summary and tags of the discount. Null until a sync has
   *  read them; tags is [] once read when the discount has none. */
  shopify_summary?: string | null;
  shopify_tags?: string[] | null;
}

export type ShopifyCouponState = 'active' | 'scheduled' | 'expired';

/** Body of PATCH /campaigns/{id}/shopify/coupons/{coupon_id}/config — the
 *  gear-icon config-edit endpoint. Deliberately has NO code/coupon_name
 *  field: identity changes only happen through replace. */
export interface EditCouponConfigInput extends ShopifyDiscountKindTerms {
  /** The coupon's `edit_version` when the modal opened; the BE refuses the
   *  save with SHOPIFY_EDIT_STALE if the coupon changed since. */
  expected_version?: string;
  value_type: ShopifyDiscountValueType;
  discount_pct: number;
  discount_amount: number;
  target_type: ShopifyTargetType;
  products: ShopifyProductRef[];
  /** Specific variants, with or instead of whole products. */
  variants: ShopifyVariantRef[];
  collections: ShopifyCollectionRef[];
  minimum_requirement_type: ShopifyMinimumRequirementType;
  minimum_requirement_value: number;
  combines_with_order_discounts: boolean;
  combines_with_product_discounts: boolean;
  combines_with_shipping_discounts: boolean;
  start_date?: string;
  /** `null` clears the end date; only meaningful when editing a turned-off code. */
  end_date?: string | null;
  usage_limit?: number;
  applies_once_per_customer: boolean;
  applies_on_each_item: boolean;
  purchase_type: ShopifyPurchaseType;
  recurring_cycle_limit: number;
}

/** Response of PATCH /campaigns/{id}/shopify/coupons/{coupon_id}/config. */
export interface EditCouponConfigResult {
  status: string;
  /** Creator links rebuilt for a new landing page, and those that failed. */
  tracking_links_updated: number;
  tracking_links_failed: number;
  /** Links that kept their page because their creator is live. */
  tracking_links_locked?: number;
  /** Off-code edits only: the state Shopify derived from the dates after the
   *  save. Absent for an edit of an assigned code. */
  shopify_state?: ShopifyCouponState;
  /** True only when `shopify_state` is `expired`. */
  turned_off?: boolean;
}

/** Response of POST .../coupons/{coupon_id}/reactivate. */
export interface ReactivateCouponResult {
  status: string;
  /** The campaign's previous live code, turned off by a confirmed swap. */
  turned_off_code?: string;
}

/** Body of POST /campaigns/{id}/shopify/coupons/{coupon_id}/replace — the
 *  edit-pencil replace endpoint. Mirrors the full editable settings for the
 *  brand-new coupon this creator (or, at campaign-wide scope, the whole
 *  campaign) gets. `mode` is intentionally absent: replace always preserves
 *  the OLD coupon's scope. */
export interface ReplaceCouponInput extends ShopifyDiscountKindTerms {
  coupon_name: string;
  value_type: ShopifyDiscountValueType;
  discount_pct: number;
  discount_amount: number;
  include_pct_in_code: boolean;
  target_type: ShopifyTargetType;
  products: ShopifyProductRef[];
  /** Specific variants, with or instead of whole products. */
  variants: ShopifyVariantRef[];
  collections: ShopifyCollectionRef[];
  minimum_requirement_type: ShopifyMinimumRequirementType;
  minimum_requirement_value: number;
  combines_with_order_discounts: boolean;
  combines_with_product_discounts: boolean;
  combines_with_shipping_discounts: boolean;
  start_date?: string;
  end_date?: string;
  usage_limit?: number;
  applies_once_per_customer: boolean;
  applies_on_each_item: boolean;
  purchase_type: ShopifyPurchaseType;
  recurring_cycle_limit: number;
}

/** Response of POST /campaigns/{id}/shopify/coupons/{coupon_id}/replace.
 *  Narrowed per the backend plan's Task 6: the backend now cleans up the old
 *  coupon automatically, so `needs_confirm`/`old_coupon_id` no longer exist —
 *  the FE's post-replace "also revoke?" prompt that depended on them has
 *  since been removed (frontend plan Task 5). */
export interface RenameCouponInput {
  couponId: string;
  code: string;
  /** The coupon's `edit_version` when the dialog opened. */
  expectedVersion: string | undefined;
  /** Picks the live-lock wording for a SHOPIFY_CREATOR_LIVE refusal; not sent. */
  scope: 'creator' | 'campaign';
}

/** Response of POST /campaigns/{id}/shopify/coupons/{coupon_id}/rename. */
export interface RenameCouponResult {
  status: string;
  coupon: ShopifyCampaignCoupon;
}

export interface ReplaceCouponResult {
  status: string;
  new_coupon: ShopifyCampaignCoupon;
  /** Replace's Shopify-side cleanup of the OLD code is best-effort: on failure
   *  the BE logs and still returns 200, because the replace the brand asked for
   *  did happen (coupon_handler.go Replace). This flags that one case, where
   *  the DB says replaced while the old code is still redeemable — the only
   *  way the brand can find out, and the only way they can act on it. */
  old_code_still_active?: boolean;
  /** The old code was turned off in Shopify but Discovr could not record it, so
   *  it may keep showing as working until the next sync. */
  old_code_end_unconfirmed?: boolean;
  /** The old code, present only alongside old_code_still_active or old_code_end_unconfirmed. */
  old_code?: string;
}

export interface ShopifyGenerateResult {
  generated: number;
  skipped: number;
  /** Skipped because Shopify renamed their code; not in `skipped`. */
  renamed?: number;
  /** Why creators were skipped; `skipped` is already_live + deactivated + in_progress. */
  skipped_by?: { already_live?: number; deactivated?: number; in_progress?: number; on_hold?: number };
  /** The codes created by this run (`coupons` is every code on the campaign). */
  created_codes?: string[];
  /** The time budget stopped the run early; `remaining` creators still need codes. */
  partial?: boolean;
  remaining?: number;
  /** Creators whose code could not be created; absent on older servers. */
  failed?: number;
  coupons: ShopifyCampaignCoupon[];
}

/** POST /campaigns/{id}/shopify/coupons/refresh response. */
export interface ShopifyRefreshResult {
  discounts: number;
  codes_unchecked: number;
  codes_deferred?: number;
  codes_renamed?: number;
  renames_ambiguous?: number;
}

/** POST /campaigns/{id}/shopify/coupons/invalidate-many response. */
export interface ShopifyInvalidateManyResult {
  invalidated: number;
  /** Requested codes that were not turned off (0 on a clean run). */
  failed: number;
}

/** One code's outcome in POST /campaigns/{id}/shopify/coupons/invalidate. */
export type ShopifyTurnOffOutcome =
  | 'turned_off'
  | 'already_off'
  | 'skipped_editing'
  | 'failed';

/** POST /campaigns/{id}/shopify/coupons/invalidate response. Counts and
 *  status cover every code; `codes` lists the ones the caller can see. */
export interface ShopifyInvalidateAllResult {
  status: 'invalidated' | 'partially_invalidated' | 'not_invalidated';
  turned_off: number;
  already_off: number;
  /** Left on because an edit of them is in flight. */
  skipped_editing: number;
  /** Left on: may still work at checkout. */
  failed: number;
  codes: { coupon_id: string; code: string; scope: string; outcome: ShopifyTurnOffOutcome }[];
}

const base = (id: string) => `/campaigns/${encodeURIComponent(id)}/shopify`;

async function getData<T>(url: string): Promise<T> {
  const res = await apiClient.get<DataResponse<T>>(url);
  return res.data.data;
}

// The list may already differ from what the brand saw: an edit holds the code,
// or Shopify renamed the campaign's code since the list loaded.
const LIST_STALE_CODES: ReadonlySet<string> = new Set([
  'SHOPIFY_NOT_CONNECTED',
  'SHOPIFY_STORE_MISMATCH',
  'SHOPIFY_UNINSTALL_IN_PROGRESS',
  'SHOPIFY_EDIT_IN_PROGRESS',
  'SHOPIFY_CAMPAIGN_CODE_RENAMED',
  'SHOPIFY_CREATOR_CODE_RENAMED',
  'SHOPIFY_DATA_REMOVED',
  'SHOPIFY_STORE_FROZEN',
]);

const toastOutcome = ({ kind, message }: OutcomeMessage) => {
  if (kind === 'error') toast.error(message);
  else if (kind === 'warning') toast.warning(message, { duration: 15_000 });
  else toast.success(message);
};

const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

// Refusals the BE words in lower case and without a full stop.
const BE_REFUSALS: Readonly<Record<string, string>> = {
  'connect your Shopify store first': 'Connect your Shopify store first.',
  'enable Shopify coupons for this campaign first': 'Turn on Shopify discount codes for this campaign first.',
  'select at least one product before generating discount codes':
    'Select at least one product before generating discount codes.',
  'select at least one collection before generating discount codes':
    'Select at least one collection before generating discount codes.',
};

const failMsg = (err: unknown, fallback: string, write = true): string => {
  if (!(err instanceof ApiError)) return fallback;
  // A dropped or timed-out request (or a proxy 5xx with no body) carries only transport text.
  if (err.code === 'NETWORK_ERROR' || (err.code === 'UNKNOWN' && GATEWAY_STATUSES.has(err.httpStatus))) {
    return write ? `${fallback.replace(/[.!?]$/, '')}. ${shopifyCopy.writeUncertain}` : fallback;
  }
  // Every coupon writer can be refused while an edit of the code is in flight.
  if (err.code === 'SHOPIFY_EDIT_IN_PROGRESS') return shopifyEditCopy.errors.editInProgress;
  if (err.code === 'SHOPIFY_DATA_REMOVED') return shopifyEditCopy.errors.dataRemoved;
  if (err.code === 'SHOPIFY_STORE_FROZEN') return shopifyCopy.storeFrozen;
  if (err.httpStatus === 409 && err.code === 'SHOPIFY_CODE_TAKEN') return shopifyEditCopy.errors.codeTaken;
  const specific = renamedConflictMessage(err) ?? dateRuleMessage(err);
  if (specific) return specific;
  return (Object.hasOwn(BE_REFUSALS, err.message) ? BE_REFUSALS[err.message] : undefined) ??
    errorMessage(err, fallback);
};

/** Config + connection state + coupon counts for the campaign Shopify tab. */
export function useCampaignShopifyConfig(campaignId: string) {
  return useQuery({
    queryKey: shopifyKeys.campaignConfig(campaignId),
    queryFn: () => getData<CampaignShopifyConfig>(base(campaignId)),
    enabled: !!campaignId,
  });
}

/** Search the connected shop's products for the targeting picker. q="" → first page. */
export function useCampaignShopifyProducts(campaignId: string, query: string, enabled = true) {
  return useQuery({
    queryKey: shopifyKeys.campaignProducts(campaignId, query),
    queryFn: () =>
      getData<ShopifyProductPage>(`${base(campaignId)}/products?q=${encodeURIComponent(query)}`),
    enabled: enabled && !!campaignId,
    staleTime: 30_000,
    // A 500 / 403 from Shopify (e.g. token lacks read_products) would retry
    // three times by default, spamming the network. One retry is enough to
    // cover a transient blip; the picker surfaces the error state instead.
    retry: 1,
    // Query key includes the debounced search term, so every keystroke swaps
    // it — without this, v5 clears `data` to undefined on each swap and the
    // picker's dropdown flashes to "Loading…" between every result set.
    // Keeping the previous page visible during refetch avoids that flicker.
    placeholderData: keepPreviousData,
  });
}

/** Paginated product search backing the "Browse products" dialog — Shopify's
 *  own picker for this screen is a bigger, scrollable, "load more" browsing
 *  experience rather than the compact inline dropdown useCampaignShopifyProducts
 *  backs; this is that same search endpoint accumulated page over page via
 *  React Query's built-in pagination (no manual effect-driven accumulation). */
export function useCampaignShopifyProductsBrowse(
  campaignId: string,
  query: string,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: shopifyKeys.campaignProductsBrowse(campaignId, query),
    queryFn: ({ pageParam }: { pageParam: string }) =>
      getData<ShopifyProductPage>(
        `${base(campaignId)}/products?q=${encodeURIComponent(query)}&after=${encodeURIComponent(pageParam)}`,
      ),
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.has_next_page ? lastPage.end_cursor : undefined),
    enabled: enabled && !!campaignId,
    // Matches the three sibling search hooks here: keep the current results on
    // screen while a debounced keystroke refetches, instead of blanking the
    // dialog to "Loading…" on every character.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: 1,
  });
}

/** One product's variants for the picker, page by page. */
export function useCampaignShopifyProductVariants(
  campaignId: string,
  productId: string,
  enabled = true,
) {
  const numericId = productId.split('/').pop() ?? '';
  return useInfiniteQuery({
    queryKey: shopifyKeys.campaignProductVariants(campaignId, productId),
    queryFn: ({ pageParam }: { pageParam: string }) =>
      getData<ShopifyVariantPage>(
        `${base(campaignId)}/products/${encodeURIComponent(numericId)}/variants?after=${encodeURIComponent(pageParam)}`,
      ),
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.has_next_page ? lastPage.end_cursor : undefined),
    enabled: enabled && !!campaignId && !!numericId,
    staleTime: 30_000,
    retry: 1,
  });
}

/** Search the connected shop's collections for the targeting picker. */
export function useCampaignShopifyCollections(campaignId: string, query: string, enabled = true) {
  return useQuery({
    queryKey: shopifyKeys.campaignCollections(campaignId, query),
    queryFn: () =>
      getData<ShopifyCollectionPage>(
        `${base(campaignId)}/collections?q=${encodeURIComponent(query)}`,
      ),
    enabled: enabled && !!campaignId,
    staleTime: 30_000,
    retry: 1,
    // Same debounced-search-term flicker as useCampaignShopifyProducts above.
    placeholderData: keepPreviousData,
  });
}

/** Finalized creators for the "choose your creators" generation picker. */
export function useCampaignShopifyCreators(campaignId: string, query: string, enabled = true) {
  return useQuery({
    queryKey: shopifyKeys.campaignCreators(campaignId, query),
    queryFn: () =>
      getData<ShopifyPickerCreator[]>(
        `${base(campaignId)}/creators?q=${encodeURIComponent(query)}`,
      ),
    enabled: enabled && !!campaignId,
    staleTime: 30_000,
    retry: 1,
    // Same debounced-search-term flicker as useCampaignShopifyProducts above.
    placeholderData: keepPreviousData,
  });
}

/**
 * Whether the campaign's currently-configured product still exists in the
 * connected store (item D). Only meaningful when store_state is "connected";
 * the BE returns exists:true in every other case.
 */
export function useCampaignShopifyProductStatus(campaignId: string, enabled = true) {
  return useQuery({
    queryKey: shopifyKeys.campaignProductStatus(campaignId),
    queryFn: () => getData<{ exists: boolean; missing_ids?: string[] }>(`${base(campaignId)}/product-status`),
    enabled: enabled && !!campaignId,
    staleTime: 30_000,
  });
}

/** Sets a removed campaign up again; resolves with the refetched config (undefined when
 *  that refetch failed after the set-up went through) so the caller can confirm
 *  the campaign is no longer removed. */
export function useShopifySetupAgain(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (shopDomain: string) => {
      const res = await apiClient.post<DataResponse<ShopifySetupAgainResult>>(
        `${base(campaignId)}/setup-again`,
        { shop_domain: shopDomain },
      );
      const config = await qc
        .fetchQuery({
          queryKey: shopifyKeys.campaignConfig(campaignId),
          queryFn: () => getData<CampaignShopifyConfig>(base(campaignId)),
          staleTime: 0,
        })
        .catch(() => undefined);
      return { ...res.data.data, config };
    },
    onSettled: () => {
      refreshCampaignShopify(qc, campaignId);
      void qc.invalidateQueries({ queryKey: shopifyKeys.summary() });
      void qc.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) });
    },
  });
}

/** Save / generate / invalidate mutations; each toasts + refreshes config, coupons and conversions. */
export function useCampaignShopifyMutations(campaignId: string) {
  const qc = useQueryClient();
  const refresh = () => refreshCampaignShopify(qc, campaignId);

  // Every coupon writer can be refused while an edit of the code holds it, or
  // after the edit was applied but its claim was lost: either way the list may
  // already differ from what the brand last saw, so it is reloaded.
  const failToast = (err: unknown, fallback: string, override?: string) => {
    if (err instanceof ApiError && LIST_STALE_CODES.has(err.code)) refresh();
    toast.error(override ?? failMsg(err, fallback));
  };

  const saveConfig = useMutation({
    mutationFn: async (input: CampaignShopifyConfigInput) => {
      const res = await apiClient.put<DataResponse<CampaignShopifyConfig>>(base(campaignId), input);
      return res.data.data;
    },
    onSuccess: () => {
      refresh();
      // The saved target may differ from the one the stale existence check
      // ran against — refetch so a "no longer available" banner clears
      // immediately after switching to a valid product/collection.
      void qc.invalidateQueries({ queryKey: shopifyKeys.campaignProductStatus(campaignId) });
      // It's genuinely saved now — the local unsaved-edit draft would only
      // ever shadow this fresh server state on a later remount.
      clearShopifyDraft(campaignId);
      toast.success('Shopify settings saved');
    },
    onError: (err) => failToast(err, 'Couldn’t save Shopify settings'),
  });

  const generate = useMutation({
    // rosterEntryIds: the "choose your creators" subset. Empty/omitted =
    // everyone ("skip this step to include everyone").
    mutationFn: async (rosterEntryIds?: string[]) => {
      const body = rosterEntryIds?.length ? { roster_entry_ids: rosterEntryIds } : {};
      const res = await apiClient.post<DataResponse<ShopifyGenerateResult>>(
        `${base(campaignId)}/coupons/generate`,
        body,
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      toastOutcome(generateResultMessage(res));
    },
    onError: (err) => {
      // The BE may have saved some codes before failing.
      refresh();
      const failed = err instanceof ApiError ? (err.details as { failed?: number } | undefined)?.failed : undefined;
      const fallback = 'Couldn’t generate discount codes';
      failToast(err, fallback, failed && failed > 0 ? `${failMsg(err, fallback).replace(/[.!?]$/, '')}. ${generateReviewCopy.failedNote(failed)}` : undefined);
    },
  });

  // Destructive actions carry a GitHub-style typed confirmation matching the
  // action's own verb IN THE DIALOG. The wire body stays the fixed
  // {"confirm":"DEACTIVATE"} the BE's Revoke endpoint independently verifies
  // server-side (SHOPIFY_CONFIRM_MISMATCH 400) for every scope — Revoke
  // itself decides `invalid` vs `deleted` from the coupon's own scope, not
  // from this confirm string, so displaying "DELETE" for creator-scope in
  // the dialog (ShopifySettingsTab's confirmWord prop) is purely a
  // user-facing safety word; it never needs to reach the network layer.
  const DEACTIVATE_CONFIRM_BODY = { confirm: 'DEACTIVATE' } as const;

  const invalidateOne = useMutation({
    mutationFn: async ({ id }: { id: string; scope: 'creator' | 'campaign' }) => {
      const res = await apiClient.delete<DataResponse<{ status: string }>>(
        `${base(campaignId)}/coupons/${encodeURIComponent(id)}`,
        { data: DEACTIVATE_CONFIRM_BODY },
      );
      return res.data.data;
    },
    onSuccess: () => {
      refresh();
      toast.success('Code deactivated');
    },
    onError: (err) => failToast(err, 'Couldn’t deactivate the code'),
  });

  // Destructive, permanent delete of a code's own discount, either scope. Unlike invalidateOne, a failed request here means
  // nothing changed on either side (the BE blocks on Shopify success before
  // writing its DB) — no optimistic UI needed, refresh() after success is
  // enough. Same typed-confirmation pattern as invalidateOne above, with its
  // own BE-verified confirm string.
  const DELETE_CONFIRM_BODY = { confirm: 'DELETE' } as const;

  const deleteCoupon = useMutation({
    mutationFn: async (couponId: string) => {
      const res = await apiClient.delete<DataResponse<{ status: string }>>(
        `${base(campaignId)}/coupons/${encodeURIComponent(couponId)}/delete`,
        { data: DELETE_CONFIRM_BODY },
      );
      return res.data.data;
    },
    onSuccess: () => {
      refresh();
      toast.success('Code deleted');
    },
    onError: (err) => failToast(err, 'Couldn’t delete the code'),
  });

  // Reactivate — turns a deactivated or replaced code (either scope) back on.
  // turn_off_live confirms the swap when another code holds its live slot: the
  // campaign's one code, or the creator's.
  const reactivateCoupon = useMutation({
    mutationFn: async ({
      id,
      end_date,
      turn_off_live = false,
    }: {
      id: string;
      end_date?: string;
      turn_off_live?: boolean;
    }) => {
      const body: { end_date?: string; turn_off_live?: boolean } = {};
      if (end_date) body.end_date = end_date;
      if (turn_off_live) body.turn_off_live = true;
      const res = await apiClient.post<DataResponse<ReactivateCouponResult>>(
        `${base(campaignId)}/coupons/${encodeURIComponent(id)}/reactivate`,
        body,
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      toast.success(
        res?.turned_off_code
          ? 'Discount code reactivated. The previous live code was deactivated.'
          : 'Discount code reactivated',
      );
    },
    onError: (err) => {
      // Another code went live since this list loaded: reload so the dialog
      // can name it and offer the swap.
      if (err instanceof ApiError && err.code === 'SHOPIFY_CAMPAIGN_CODE_LIVE') refresh();
      // The dialog names the creator's other live code and offers the swap.
      if (creatorLiveCode(err)) return;
      failToast(err, 'Couldn’t reactivate discount code');
    },
  });

  // Bulk toolbar action over one scope: the page splits a mixed selection, so
  // each call's counts describe one operation (creator codes are deleted,
  // campaign codes are deactivated). The wire body is the same for both.
  const invalidateMany = useMutation({
    mutationFn: async ({ ids }: { ids: string[]; scope: 'creator' | 'campaign' }) => {
      const res = await apiClient.post<DataResponse<ShopifyInvalidateManyResult>>(
        `${base(campaignId)}/coupons/invalidate-many`,
        { ...DEACTIVATE_CONFIRM_BODY, coupon_ids: ids },
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      toastOutcome(invalidateManyMessage(res));
    },
    onError: (err) => failToast(err, 'Couldn’t deactivate the selected codes'),
  });

  // Deactivate all codes: every code of the campaign, reported per code.
  const invalidateAll = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<DataResponse<ShopifyInvalidateAllResult>>(
        `${base(campaignId)}/coupons/invalidate`,
        DEACTIVATE_CONFIRM_BODY,
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      toastOutcome(deactivateAllMessage(res));
    },
    onError: (err) => {
      refresh();
      failToast(err, shopifyDeactivateAllCopy.result.failedGeneric);
    },
  });

  // Config-edit: edits a coupon's own Shopify discount terms in place.
  // Never touches code/coupon_name (identity fields) — that's what replace
  // is for. No draft-only gate; an already-sent coupon's config is the case
  // this endpoint exists for.
  const editCouponConfig = useMutation({
    mutationFn: async (input: { couponId: string; input: EditCouponConfigInput }) => {
      const res = await apiClient.patch<DataResponse<EditCouponConfigResult>>(
        `${base(campaignId)}/coupons/${encodeURIComponent(input.couponId)}/config`,
        input.input,
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      // An off-code edit reports the state Shopify derived; an assigned-code edit has none.
      const updated = editSavedMessage(res.shopify_state)?.replace(/\.$/, '') ?? 'Discount code updated';
      const failed = res.tracking_links_failed ?? 0;
      const locked = res.tracking_links_locked ?? 0;
      const withLockedNote = (text: string) =>
        locked > 0
          ? `${text}${text.endsWith('.') ? '' : '.'} ${shopifyEditCopy.live.linksKept(locked)}`
          : text;
      if (failed > 0) {
        // The backend rebuilds stale links on every save, so saving again retries.
        toast.warning(
          withLockedNote(
            `${updated}, but ${failed} creator link${failed === 1 ? '' : 's'} still point${failed === 1 ? 's' : ''} to the old page. Save the discount again to fix ${failed === 1 ? 'it' : 'them'}.`,
          ),
          { duration: 15_000 },
        );
        return;
      }
      toast.success(withLockedNote(editSavedMessage(res.shopify_state) ?? updated));
    },
    onError: (err) => {
      const failure = editConfigFailure(err);
      if (failure.refreshList) refresh();
      toast.error(failure.message);
    },
  });

  // Replace (edit pencil): orphans the old coupon (DB-only) and mints a
  // brand-new one for the same creator (or, at campaign-wide scope, the
  // whole campaign) through the BE's Generate flow. 409s with
  // SHOPIFY_CREATOR_LIVE once the affected creator is publishing live.
  // Shopify-side cleanup of the old coupon's discount now happens
  // automatically on the backend — there's nothing left for the FE to react
  // to in the response.
  const replaceCoupon = useMutation({
    mutationFn: async (input: { couponId: string; input: ReplaceCouponInput }) => {
      const res = await apiClient.post<DataResponse<ReplaceCouponResult>>(
        `${base(campaignId)}/coupons/${encodeURIComponent(input.couponId)}/replace`,
        input.input,
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      // Item 140. Silence here left the brand believing the old code was off
      // while it was still redeemable at checkout — the one outcome they have
      // to act on themselves, since Discovr can no longer reach it.
      if (res.old_code_still_active) {
        toast.warning(
          res.old_code
            ? `New code created, but the old code ${res.old_code} couldn't be deactivated in Shopify. Delete it in Shopify admin (Discounts).`
            : "New code created, but the old code couldn't be deactivated in Shopify. Delete it in Shopify admin (Discounts).",
          { duration: 10_000 },
        );
        return;
      }
      if (res.old_code_end_unconfirmed) {
        toast.warning(
          `New code created. The old code${res.old_code ? ` ${res.old_code}` : ''} was turned off in Shopify, but Discovr couldn't confirm it yet, so it may still show as working. Use Refresh from Shopify to update it.`,
          { duration: 10_000 },
        );
        return;
      }
      toast.success('Discount code replaced');
    },
    onError: (err) =>
      failToast(
        err,
        'Couldn’t replace discount code',
        dateRuleMessage(err) === shopifyEditCopy.errors.endNotFuture
          ? shopifyEditCopy.errors.replaceEndPassed
          : undefined,
      ),
  });

  // Rename: same Shopify discount, new code text. The old code stops working.
  const renameCoupon = useMutation({
    mutationFn: async (input: RenameCouponInput) => {
      const res = await apiClient.post<DataResponse<RenameCouponResult>>(
        `${base(campaignId)}/coupons/${encodeURIComponent(input.couponId)}/rename`,
        { code: input.code, expected_version: input.expectedVersion },
      );
      return res.data.data;
    },
    onSuccess: () => {
      refresh();
      toast.success(shopifyRenameCopy.renamed);
    },
    onError: (err, input) => {
      const failure = renameFailure(err, input.scope);
      if (failure.refreshList || (err instanceof ApiError && LIST_STALE_CODES.has(err.code))) refresh();
      if (!failure.field) toast.error(failure.message);
    },
  });

  const refreshFromShopify = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<DataResponse<ShopifyRefreshResult>>(
        `${base(campaignId)}/coupons/refresh`,
      );
      return res.data.data;
    },
    onSuccess: (res) => {
      refresh();
      toastOutcome(refreshResultMessage(res ?? {}));
    },
    onError: (err) =>
      // Throttled, not failed: the server says when the next refresh is allowed.
      err instanceof ApiError && err.code === 'SHOPIFY_REFRESH_TOO_SOON'
        ? toast.info(err.message)
        : toast.error(failMsg(err, 'Couldn’t refresh from Shopify', false)),
  });

  return {
    saveConfig,
    generate,
    invalidateOne,
    invalidateMany,
    invalidateAll,
    deleteCoupon,
    reactivateCoupon,
    editCouponConfig,
    replaceCoupon,
    renameCoupon,
    refreshFromShopify,
  };
}

/**
 * GET /campaigns/{id}/shopify/config/duplicate?from={source} — the source
 * campaign's Shopify setup in the config PUT's shape, no codes. {id} is the
 * target. Persists nothing: the caller loads the result into the unsaved form.
 */
export function useCampaignShopifyDuplicate(campaignId: string) {
  return useMutation({
    mutationFn: (sourceCampaignId: string) =>
      getData<CampaignShopifyConfigInput>(
        `${base(campaignId)}/config/duplicate?from=${encodeURIComponent(sourceCampaignId)}`,
      ),
  });
}

/**
 * GET /campaigns/{id}/shopify/config/duplicate-sources — every other campaign
 * with a Shopify setup on this store, each marked eligible or refused with a
 * plain-words reason. {id} is the target.
 */
export function useCampaignShopifyDuplicateSources(campaignId: string) {
  return useQuery({
    queryKey: shopifyKeys.duplicateSources(campaignId),
    queryFn: () =>
      getData<ShopifyDuplicateSources>(`${base(campaignId)}/config/duplicate-sources`),
    enabled: !!campaignId,
  });
}

/**
 * Toggle per-campaign Shopify enablement via the dedicated
 * `PUT /campaigns/{id}/shopify/enable` endpoint.
 *
 * On success invalidates:
 *  - `campaignKeys.detail(campaignId)` — refreshes `integrations.shopify.enabled` for nav gating
 *  - `shopifyKeys.campaignConfig(campaignId)` — refreshes the Shopify tab's config state
 *  - `shopifyKeys.glance()` — the brand-wide figures
 */
export function useCampaignShopifyEnable(campaignId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiClient.put<DataResponse<{ enabled: boolean }>>(
        `${base(campaignId)}/enable`,
        { enabled },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) });
      void qc.invalidateQueries({ queryKey: shopifyKeys.campaignConfig(campaignId) });
      void qc.invalidateQueries({ queryKey: shopifyKeys.glance() });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'SHOPIFY_DATA_REMOVED') {
        void qc.invalidateQueries({ queryKey: shopifyKeys.campaignConfig(campaignId) });
      }
      toast.error(failMsg(err, "Couldn't update Shopify enablement", false));
    },
  });
}
